import { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Stack,
    FormSelector,
    Icon,
    Divider,
    TextField,
} from '../../../../base';
import { Autocomplete } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    ModalContainer,
    DayButton,
    TimeWindowContainer,
    AddAvailabilityButton,
    RemoveButton,
    ActionButton,
    BackButton,
    AvailabilityScrollContainer,
    CloseButton,
    LoadingOverlay,
    LoadingSpinner,
    SuccessIconPlaceholder,
    AvailabilitySummaryList,
} from './ScheduleInterview.styles';
import { DAYS_OF_WEEK } from './ScheduleInterview.consts';
import {
    IScheduleInterviewProps,
    ScheduleInterviewStep,
} from './ScheduleInterview.interfaces';
import { useScheduleInterviewForm } from './ScheduleInterview.hooks';
import {
    formatAvailabilityForDisplay,
    IAvailabilityDisplayItem,
} from './ScheduleInterview.utils';
import { IScheduleInterviewData } from '../../../../common';

const AvailabilitySummary = ({
    items,
}: {
    items: IAvailabilityDisplayItem[];
}) => (
    <AvailabilitySummaryList>
        {items.map((item) => (
            <Typography key={item.day} variant="body2" sx={{ py: 0.25 }}>
                <strong>{item.day}</strong> {item.timeRanges}
            </Typography>
        ))}
    </AvailabilitySummaryList>
);

const ScheduleInterview = (props: IScheduleInterviewProps) => {
    const { onSubmit, onCancel, isLoading, scheduleInterviewResult } = props;
    const { t } = useTranslation();

    const [step, setStep] = useState<ScheduleInterviewStep>('form');
    const [submittedData, setSubmittedData] =
        useState<IScheduleInterviewData | null>(null);
    const [displayItems, setDisplayItems] = useState<
        IAvailabilityDisplayItem[]
    >([]);

    const handleFormComplete = (
        data: IScheduleInterviewData,
        language: string
    ) => {
        setSubmittedData(data);
        setDisplayItems(
            formatAvailabilityForDisplay(data.availability, language, t)
        );
        setStep('confirm');
    };

    const {
        control,
        selectedDay,
        availability,
        isDurationSelected,
        currentDayWindows,
        canAddMore,
        errors,
        durationOptions,
        timeOptions,
        language,
        handleDayClick,
        handleAddTimeWindow,
        handleRemoveTimeWindow,
        handleTimeWindowChange,
        getFilteredTimeOptions,
        handleSubmit,
        isDayComplete,
    } = useScheduleInterviewForm((data) =>
        handleFormComplete(data, language)
    );

    useEffect(() => {
        if (
            scheduleInterviewResult === 'success' &&
            step === 'confirm'
        ) {
            setStep('success');
        }
    }, [scheduleInterviewResult, step]);

    const handleBack = () => {
        setStep('form');
    };

    const handleConfirm = () => {
        if (submittedData) {
            onSubmit?.(submittedData);
        }
    };

    const isConfirmStep = step === 'confirm';
    const isSuccessStep = step === 'success';

    return (
        <ModalContainer>
            <CloseButton
                onClick={onCancel}
                disabled={isLoading && isConfirmStep}
            >
                <Icon name="CloseIcon" />
            </CloseButton>

            {/* ---- FORM STEP ---- */}
            {step === 'form' && (
                <form onSubmit={handleSubmit}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        {t('scheduleInterview.title')}
                    </Typography>

                    <Box sx={{ mb: 2 }}>
                        <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{ mb: 0.5 }}
                        >
                            {t('scheduleInterview.interviewDuration')}
                        </Typography>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mb: 1, display: 'block' }}
                        >
                            {t('scheduleInterview.chooseDurationDescription')}
                        </Typography>
                        <FormSelector
                            name="interviewDuration"
                            control={control}
                            options={durationOptions}
                            label={t('scheduleInterview.chooseDuration')}
                            required
                        />
                    </Box>

                    {isDurationSelected && (
                        <>
                            <Box sx={{ mb: 2 }}>
                                <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    sx={{ mb: 0.5 }}
                                >
                                    {t(
                                        'scheduleInterview.selectAvailableDays'
                                    )}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ mb: 1, display: 'block' }}
                                >
                                    {t(
                                        'scheduleInterview.selectDaysDescription'
                                    )}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: '6px' }}>
                                    {DAYS_OF_WEEK.map((day) => (
                                        <DayButton
                                            key={day.value}
                                            selected={
                                                selectedDay === day.value &&
                                                !!availability[day.value]
                                            }
                                            onClick={() =>
                                                handleDayClick(day.value)
                                            }
                                            variant="outlined"
                                            sx={{
                                                ...(isDayComplete(
                                                    day.value
                                                ) && {
                                                    backgroundColor:
                                                        'secondary.light',
                                                    '&:hover': {
                                                        backgroundColor:
                                                            'secondary.light',
                                                    },
                                                }),
                                            }}
                                        >
                                            {t(`scheduleInterview.${day.key}`)}
                                        </DayButton>
                                    ))}
                                </Box>
                            </Box>

                            {currentDayWindows.length > 0 && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography
                                        variant="body2"
                                        fontWeight={600}
                                        sx={{ mb: 0.5 }}
                                    >
                                        {t(
                                            'scheduleInterview.setAvailabilityForDay'
                                        )}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ mb: 1, display: 'block' }}
                                    >
                                        {t(
                                            'scheduleInterview.chooseTimesDescription'
                                        )}
                                    </Typography>

                                    <AvailabilityScrollContainer>
                                        {currentDayWindows.map((window) => {
                                            const fromValue = timeOptions.find(
                                                (opt) =>
                                                    opt.value === window.from
                                            );
                                            const toValue = timeOptions.find(
                                                (opt) =>
                                                    opt.value === window.to
                                            );
                                            const hasOverlapError =
                                                !!errors[
                                                    `${window.id}_overlap`
                                                ];
                                            const fromError =
                                                errors[`${window.id}_from`];
                                            const toError =
                                                errors[`${window.id}_to`];

                                            return (
                                                <Box
                                                    key={window.id}
                                                    sx={{ mb: 2 }}
                                                >
                                                    <TimeWindowContainer>
                                                        <Box
                                                            sx={{
                                                                flex: 1,
                                                                mr: 1,
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{
                                                                    display:
                                                                        'block',
                                                                    mb: 0.5,
                                                                }}
                                                            >
                                                                {t(
                                                                    'scheduleInterview.from'
                                                                )}
                                                            </Typography>
                                                            <Autocomplete
                                                                options={getFilteredTimeOptions(
                                                                    window.id,
                                                                    'from'
                                                                )}
                                                                disableClearable
                                                                onChange={(
                                                                    _e,
                                                                    val
                                                                ) => {
                                                                    if (
                                                                        val &&
                                                                        typeof val ===
                                                                            'object' &&
                                                                        'value' in
                                                                            val
                                                                    ) {
                                                                        handleTimeWindowChange(
                                                                            window.id,
                                                                            'from',
                                                                            val.value
                                                                        );
                                                                    }
                                                                }}
                                                                value={
                                                                    fromValue ||
                                                                    undefined
                                                                }
                                                                getOptionLabel={(
                                                                    option
                                                                ) =>
                                                                    option?.label ||
                                                                    ''
                                                                }
                                                                isOptionEqualToValue={(
                                                                    option,
                                                                    value
                                                                ) =>
                                                                    option.value ===
                                                                    value.value
                                                                }
                                                                renderInput={(
                                                                    params
                                                                ) => (
                                                                    <TextField
                                                                        {...params}
                                                                        placeholder={t(
                                                                            'scheduleInterview.selectTime'
                                                                        )}
                                                                        size="small"
                                                                        error={
                                                                            !!fromError ||
                                                                            hasOverlapError
                                                                        }
                                                                    />
                                                                )}
                                                            />
                                                            <Box
                                                                sx={{
                                                                    minHeight:
                                                                        '20px',
                                                                    mt: 0.5,
                                                                }}
                                                            >
                                                                {fromError && (
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="error"
                                                                    >
                                                                        {
                                                                            fromError
                                                                        }
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </Box>
                                                        <Box
                                                            sx={{
                                                                flex: 1,
                                                                mr: 1,
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{
                                                                    display:
                                                                        'block',
                                                                    mb: 0.5,
                                                                }}
                                                            >
                                                                {t(
                                                                    'scheduleInterview.to'
                                                                )}
                                                            </Typography>
                                                            <Autocomplete
                                                                options={getFilteredTimeOptions(
                                                                    window.id,
                                                                    'to'
                                                                )}
                                                                disableClearable
                                                                onChange={(
                                                                    _e,
                                                                    val
                                                                ) => {
                                                                    if (
                                                                        val &&
                                                                        typeof val ===
                                                                            'object' &&
                                                                        'value' in
                                                                            val
                                                                    ) {
                                                                        handleTimeWindowChange(
                                                                            window.id,
                                                                            'to',
                                                                            val.value
                                                                        );
                                                                    }
                                                                }}
                                                                value={
                                                                    toValue ||
                                                                    undefined
                                                                }
                                                                getOptionLabel={(
                                                                    option
                                                                ) =>
                                                                    option?.label ||
                                                                    ''
                                                                }
                                                                isOptionEqualToValue={(
                                                                    option,
                                                                    value
                                                                ) =>
                                                                    option.value ===
                                                                    value.value
                                                                }
                                                                renderInput={(
                                                                    params
                                                                ) => (
                                                                    <TextField
                                                                        {...params}
                                                                        placeholder={t(
                                                                            'scheduleInterview.selectTime'
                                                                        )}
                                                                        size="small"
                                                                        error={
                                                                            !!toError ||
                                                                            hasOverlapError
                                                                        }
                                                                    />
                                                                )}
                                                            />
                                                            <Box
                                                                sx={{
                                                                    minHeight:
                                                                        '20px',
                                                                    mt: 0.5,
                                                                }}
                                                            >
                                                                {toError && (
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="error"
                                                                    >
                                                                        {
                                                                            toError
                                                                        }
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </Box>
                                                        <RemoveButton
                                                            onClick={() =>
                                                                handleRemoveTimeWindow(
                                                                    window.id
                                                                )
                                                            }
                                                        >
                                                            <Icon name="CloseIcon" />
                                                        </RemoveButton>
                                                    </TimeWindowContainer>
                                                    {hasOverlapError && (
                                                        <Typography
                                                            variant="caption"
                                                            color="error"
                                                            sx={{
                                                                mt: 0.5,
                                                                display:
                                                                    'block',
                                                            }}
                                                        >
                                                            {
                                                                errors[
                                                                    `${window.id}_overlap`
                                                                ]
                                                            }
                                                        </Typography>
                                                    )}
                                                </Box>
                                            );
                                        })}

                                        {canAddMore && (
                                            <Box>
                                                <AddAvailabilityButton
                                                    onClick={
                                                        handleAddTimeWindow
                                                    }
                                                    startIcon={
                                                        <Icon name="AddIcon" />
                                                    }
                                                    disableRipple
                                                    disableFocusRipple
                                                >
                                                    {t(
                                                        'scheduleInterview.addAvailability'
                                                    )}
                                                </AddAvailabilityButton>
                                            </Box>
                                        )}
                                    </AvailabilityScrollContainer>
                                </Box>
                            )}

                            <Divider sx={{ my: 2 }} />

                            <Stack direction="row" justifyContent="flex-end">
                                <ActionButton
                                    variant="contained"
                                    color="secondary"
                                    type="submit"
                                >
                                    {t('scheduleInterview.previewOptions')}
                                </ActionButton>
                            </Stack>
                        </>
                    )}
                </form>
            )}

            {/* ---- CONFIRM STEP ---- */}
            {isConfirmStep && (
                <Box sx={{ position: 'relative' }}>
                    {isLoading && (
                        <LoadingOverlay>
                            <LoadingSpinner size={48} />
                        </LoadingOverlay>
                    )}

                    <Typography
                        variant="h6"
                        sx={{ mb: 1, textAlign: 'center' }}
                    >
                        {t('scheduleInterview.confirmTitle')}
                    </Typography>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2, textAlign: 'center' }}
                    >
                        {t('scheduleInterview.confirmDescription')}
                    </Typography>

                    <AvailabilitySummary items={displayItems} />

                    <Divider sx={{ my: 2 }} />

                    <Stack
                        direction="row"
                        justifyContent="space-between"
                    >
                        <BackButton
                            variant="outlined"
                            color="secondary"
                            onClick={handleBack}
                            disabled={isLoading}
                        >
                            {t('scheduleInterview.back')}
                        </BackButton>
                        <ActionButton
                            variant="contained"
                            color="secondary"
                            onClick={handleConfirm}
                            disabled={isLoading}
                        >
                            {t('scheduleInterview.confirm')}
                        </ActionButton>
                    </Stack>
                </Box>
            )}

            {/* ---- SUCCESS STEP ---- */}
            {isSuccessStep && (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                    {/* Placeholder for success icon SVG */}
                    <SuccessIconPlaceholder>
                        <Icon name="CheckIcon" />
                    </SuccessIconPlaceholder>

                    <Typography
                        variant="h6"
                        fontWeight={700}
                        sx={{ mb: 2, whiteSpace: 'pre-line' }}
                    >
                        {t('scheduleInterview.successTitle')}
                    </Typography>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                    >
                        {t('scheduleInterview.successAvailabilityIntro')}
                    </Typography>

                    <AvailabilitySummary items={displayItems} />

                    <Box sx={{ mt: 3 }}>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            {t('scheduleInterview.successFooterLine1')}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            {t('scheduleInterview.successFooterLine2')}
                        </Typography>
                    </Box>
                </Box>
            )}
        </ModalContainer>
    );
};

export default ScheduleInterview;
